import { Button, Group, UnstyledButton } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { type MouseEvent, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import CustomTableViews from 'shared/components/CustomTable/CustomTableViews';
import { Icon, IconName } from 'shared/components/Icon';
import { queryParamParser } from 'shared/utils/view';

import { ExportPaymentPlansCsv } from './ExportPaymentPlansCsv';
import { type GetPaymentPlansViewParams, useGetPaymentPlansView } from './useGetPaymentPlansView';

// Builds the view-data query params from the URL: view id from the hash, filters
// from the search — the same shape ClientsList and InvoicesList use.
const paramsFromLocation = (hash: string, search: string): GetPaymentPlansViewParams => ({
  view_id: hash.slice(1),
  ...queryParamParser(qs.parse(search, { arrayFormat: 'comma' })),
});

// A finished plan cannot be edited, so it offers no row action — the same rule the
// legacy index applies through its `!cancelled && !completed` guard.
const FINISHED_STATUS_KEYS = ['completed', 'cancelled'];

type PaymentPlanRow = { id: string | number; status_key?: string };

export const CustomPaymentPlansList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [params, setParams] = useState<GetPaymentPlansViewParams>(() =>
    paramsFromLocation(location.hash, location.search),
  );
  const { paymentPlans, isPaymentPlansInitialLoading, refetchPaymentPlans } =
    useGetPaymentPlansView(params);

  useEffect(() => {
    setParams(paramsFromLocation(location.hash, location.search));
  }, [location]);

  const showPaymentPlan = (item: PaymentPlanRow, event: MouseEvent<HTMLTableRowElement>) => {
    if (event.metaKey || event.ctrlKey || event.button === 1) {
      window.open(`/payment-plans/${item.id}`, '_blank');
      return;
    }

    history.push(`/payment-plans/${item.id}`);
  };

  const actionsCol = (item: PaymentPlanRow) => {
    if (FINISHED_STATUS_KEYS.includes(item.status_key ?? '')) return null;

    return (
      <UnstyledButton
        display="block"
        aria-label={t(i18nKeys.EDIT)}
        onClick={event => {
          event.stopPropagation();
          history.push(`/payment-plans/${item.id}/edit`);
        }}
      >
        <Icon name={IconName.SETTINGS_GEAR} size="18px" />
      </UnstyledButton>
    );
  };

  const handleClickExport = () => {
    modals.open({ title: t(i18nKeys.EXPORT_CSV), size: 'xl', children: <ExportPaymentPlansCsv /> });
  };

  return (
    <CustomTableViews
      title={t(i18nKeys.NAV.PAYMENT_PLAN)}
      tableName="payment_plans"
      actions={
        <Group gap="sm">
          <Button variant="default" onClick={handleClickExport}>
            {t(i18nKeys.EXPORT_CSV)}
          </Button>
          <Button
            onClick={() => history.push('/payment-plans/new')}
            leftSection={<IconPlus size={16} stroke={1.5} />}
          >
            {t(i18nKeys.NEW_PAYMENT_PLAN)}
          </Button>
        </Group>
      }
      onClickRow={showPaymentPlan}
      actionsCol={actionsCol}
      callbackAction={refetchPaymentPlans}
      isLoading={isPaymentPlansInitialLoading}
      pagination={paymentPlans?.metadata?.pagination}
      items={paymentPlans?.data ?? []}
    />
  );
};
